import {
	FrontendAnalystCustomGroup,
	FrontendClient,
	FrontendCompanyWithAdditionalInfomation,
	FrontendRolePermission,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	CommonSwitch,
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { validateEmail } from "../../../../helpers/inputHelper";
import { GreenButton } from "../../../../common-components/button";
import {
	createUUID,
	doesUserHaveAdminAccess,
} from "node-library/dist/helpers/request_helper";
import { SystemRole } from "node-library/dist/constants";
import { getUserRoleString } from "../../../../helpers/userHelper";
import EditAnalystPermissionModalContent from "./modalContent/editAnalystPermissionModalContent";
import {
	BatchAccessLevel,
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	addOrEditUser: (
		user: FrontendUserInfo,
		newPassword: string,
		role: SystemRole
	) => void;

	operatorUser: FrontendUserInfo;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	user?: FrontendUserInfo;
	companyCustomGroups: Array<FrontendAnalystCustomGroup>;
}

export default function AddOrEditUserModal(props: Props) {
	const { t: translation } = useTranslation();

	const { open, setOpen, operatorUser } = props;

	const [companyCustomGroups, setCompanyCustomGroups] = useState<
		Array<FrontendAnalystCustomGroup>
	>(props.companyCustomGroups);

	const [email, setEmail] = useState<string>("");
	const [firstName, setFirstName] = useState<string>("");
	const [lastName, setLastName] = useState<string>("");
	const [role, setRole] = useState<SystemRole>(SystemRole.ANALYST);

	const [userName, setUserName] = useState<string>("");

	const [changePassword, setChangePassword] = useState<boolean>(false);
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");

	const [userLocked, setUserLocked] = useState<boolean>(false);
	//validations
	const [userNameErrorMsg, setUserNameErrorMsg] = useState<string>("");

	const [emailErrorMsg, setEmailErrorMsg] = useState<string>("");
	const [passwrodErrorMsg, setPasswordErrorMsg] = useState<string>("");

	const [confirmPasswordErrorMsg, setConfirmPasswordErrorMsg] =
		useState<string>("");

	const [firstNameErrorMsg, setFirstNameErrorMsg] = useState<string>("");
	const [lastNameErrorMsg, setLastNameErrorMsg] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [newGroupId, setNewGroupId] = useState<string>(
		props.user?.analystRoleGroup?.isIndividualGroup
			? props.user?.analystRoleGroup?.groupId
			: createUUID()
	);

	const [analystPermissions, setAnalystPermissions] =
		useState<FrontendAnalystCustomGroup>({
			groupId: props.user?.analystRoleGroup?.groupId || newGroupId,
			groupName:
				props.user?.analystRoleGroup?.groupName || translation("Custom"),
			permittedBatchIds: props.user?.analystRoleGroup?.permittedBatchIds || [],

			testCategoryOtherTestPermissions:
				props.user?.analystRoleGroup?.testCategoryOtherTestPermissions || [],
			isIndividualGroup:
				props.user?.analystRoleGroup?.isIndividualGroup ?? true,
		});

	const operatorCanEditUserAccess: boolean = operatorUser
		? hasPermission(
				operatorUser?.role?.permissions as PermissionMap,
				Permissions.users.update
		  ) &&
		  (operatorUser.role?.accessLevel.administrativeLevel as number) >
				(props.user?.role?.accessLevel.administrativeLevel as number)
		: false;

	const userHasLimitedBatchAccess: boolean = [
		BatchAccessLevel.LIMITED_ACCESS,
		BatchAccessLevel.ASSIGNED_ONLY,
	].includes(
		props.user?.role?.accessLevel.limitedBatchAccess as BatchAccessLevel
	);

	const toggleCheckboxOption = (batchId: string) => {
		setAnalystPermissions((prev) => {
			const { permittedBatchIds } = prev;

			// Check if the batchId already exists
			const exists = permittedBatchIds.includes(batchId);

			// Create a new array with batchId added or removed
			const updatedBatchIds = exists
				? permittedBatchIds.filter((id) => id !== batchId)
				: [...permittedBatchIds, batchId];

			// Return updated state
			return {
				...prev,
				permittedBatchIds: updatedBatchIds,
			};
		});
	};
	const toggleOtherTestOption = (testCategoryId: string) => {
		setAnalystPermissions((prev) => {
			const { testCategoryOtherTestPermissions } = prev;

			// Check if the batchId already exists
			const exists = testCategoryOtherTestPermissions.includes(testCategoryId);

			// Create a new array with batchId added or removed
			const updatedCategoryIds = exists
				? testCategoryOtherTestPermissions.filter((id) => id !== testCategoryId)
				: [...testCategoryOtherTestPermissions, testCategoryId];

			// Return updated state
			return {
				...prev,
				testCategoryOtherTestPermissions: updatedCategoryIds,
			};
		});
	};

	const changeCustomGroup = (groupId: string) => {
		const selectedGroup = companyCustomGroups.find(
			(g) => g.groupId === groupId
		);
		if (selectedGroup) {
			setAnalystPermissions(selectedGroup);
		} else {
			setAnalystPermissions({
				groupId: newGroupId,
				groupName: translation("Custom"),
				permittedBatchIds: [],
				testCategoryOtherTestPermissions: [],
				isIndividualGroup: true,
			});
		}
	};

	const fieldsForRegistration: Array<any> = [
		{
			id: "user-username-input",

			label: `${translation("Username")}`,
			inputType: "text",
			value: userName,
			setValue: setUserName,
			required: true,
			disabled: props.user ? true : false,
			errorMsg: userNameErrorMsg,
			setErrorMsg: setUserNameErrorMsg,
		},
		{
			id: "user-firstname-input",

			label: `${translation("FirstName")}`,
			inputType: "text",
			value: firstName,
			setValue: setFirstName,
			required: true,
			errorMsg: firstNameErrorMsg,
			setErrorMsg: setFirstNameErrorMsg,
		},
		{
			id: "user-lastname-input",

			label: `${translation("LastName")}`,
			inputType: "text",
			value: lastName,
			setValue: setLastName,
			required: true,
			errorMsg: lastNameErrorMsg,
			setErrorMsg: setLastNameErrorMsg,
		},
		{
			id: "user-email-input",
			label: `${translation("Email")}`,
			inputType: "email",
			value: email,
			setValue: setEmail,
			required: true,
			errorMsg: emailErrorMsg,
			setErrorMsg: setEmailErrorMsg,
		},
	];

	const passwordFields = [
		{
			id: "user-password-input",
			label: `${translation("Password")}`,
			inputType: "password",
			value: password,
			setValue: setPassword,
			required: true,
			errorMsg: passwrodErrorMsg,
			setErrorMsg: setPasswordErrorMsg,
		},
		{
			id: "user-confirm-password-input",
			label: `${translation("ConfirmPassword")}`,
			inputType: "password",
			value: confirmPassword,
			setValue: setConfirmPassword,
			required: true,
			errorMsg: confirmPasswordErrorMsg,
			setErrorMsg: setConfirmPasswordErrorMsg,
		},
	];

	useEffect(() => {
		if (open) {
			for (const field of fieldsForRegistration) {
				field.setErrorMsg(translation(""));
			}
			setCurrentPageIndex(0);
			setEmail(props.user?.email || "");
			setFirstName(props.user?.firstName || "");
			setLastName(props.user?.lastName || "");
			setUserName(props.user?.userName || "");
			setUserLocked(props.user?.locked || false);
			setRole(props.user?.role?.roleKey || SystemRole.ANALYST);
			setChangePassword(false);
			setPassword("");
			setConfirmPassword("");

			setCompanyCustomGroups(props.companyCustomGroups);
			if (props.user?.analystRoleGroup) {
				setCompanyCustomGroups([
					...props.companyCustomGroups,
					props.user?.analystRoleGroup,
				]);
			}
			const newGroupId = props.user?.analystRoleGroup?.isIndividualGroup
				? props.user?.analystRoleGroup?.groupId
				: createUUID();

			setNewGroupId(newGroupId);
			setAnalystPermissions({
				groupId: props.user?.analystRoleGroup?.groupId || newGroupId,
				groupName:
					props.user?.analystRoleGroup?.groupName || translation("Custom"),
				permittedBatchIds:
					props.user?.analystRoleGroup?.permittedBatchIds || [],
				testCategoryOtherTestPermissions:
					props.user?.analystRoleGroup?.testCategoryOtherTestPermissions || [],
				isIndividualGroup:
					props.user?.analystRoleGroup?.isIndividualGroup ?? true,
			});
		}
	}, [open]);

	function handleSubmitUserInfo() {
		let hasError: boolean = false;
		for (const field of fieldsForRegistration) {
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length == 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			} else {
				field.setErrorMsg("");
			}
		}
		if (!props.user || changePassword) {
			for (const field of passwordFields) {
				if (
					field.required &&
					(!field.value || (field.value as string).trim().length == 0)
				) {
					field.setErrorMsg(translation("MissingInformation"));
					hasError = true;
				} else {
					field.setErrorMsg("");
				}
			}
			if (password !== confirmPassword) {
				setConfirmPasswordErrorMsg("PasswordDoesNotMatch");
				return;
			}
		}

		if (hasError) {
			return;
		}
		if (!validateEmail(email)) {
			setEmailErrorMsg(translation("InvalidEmail"));
			return;
		}

		if (userHasLimitedBatchAccess) {
			setCurrentPageIndex(1);
		} else {
			handleSubmit();
		}
	}

	function handleSubmit() {
		let newUser: FrontendUserInfo;
		const matchingRole: FrontendRolePermission | undefined = (
			props.company.systemRolePermissions || []
		).find((rolePermission) => {
			return rolePermission.roleKey === role;
		});
		if (props.user) {
			newUser = {
				...props.user,
				email,
				firstName,
				lastName,
				role: matchingRole,
				locked: userLocked,
			};
		} else {
			newUser = {
				userId: createUUID(),
				userName,
				passwordSet: false,
				email,
				firstName,
				lastName,
				role: matchingRole,
				locked: userLocked,
			};
		}
		let newPassword: string = "";
		if (!props.user) {
			newPassword = password;
		} else if (changePassword) {
			newPassword = password;
		}
		if (userHasLimitedBatchAccess) {
			//put the group in
			let group: FrontendAnalystCustomGroup;
			if (analystPermissions.groupId) {
				group = analystPermissions;
			} else {
				group = { ...analystPermissions, groupId: createUUID() };
			}
			newUser.analystRoleGroup = group;
		}
		props.addOrEditUser(newUser, newPassword, role);
		setOpen(false);
	}

	const registrationInputs: Array<React.ReactNode> = fieldsForRegistration.map(
		(field, index) => {
			return (
				<CustomTextInput
					id={field.id}
					key={field.id}
					value={field.value}
					setValue={field.setValue}
					label={field.label}
					type={field.inputType}
					required={field.required}
					disabled={field.disabled}
					errorMsg={field.errorMsg}
					setErrorMsg={field.setErrorMsg}
				></CustomTextInput>
			);
		}
	);

	const passwordInputs: Array<React.ReactNode> = passwordFields.map(
		(field, index) => {
			return (
				<CustomTextInput
					id={field.id}
					key={field.id}
					value={field.value}
					setValue={field.setValue}
					label={field.label}
					type={field.inputType}
					required={field.required}
					errorMsg={field.errorMsg}
					setErrorMsg={field.setErrorMsg}
				></CustomTextInput>
			);
		}
	);
	const lockUserField = {
		label: `${translation("LockUser")}:`,
		description: `${translation("LockUserDescription")}`,
		inputType: "checkbox",
		checked: userLocked,
		setChecked: setUserLocked,
	};
	const lockUserSwitch: React.ReactNode = (
		<div className="common-card-header">
			<div className="typography-heading-03">{translation("LockUser")}</div>
			<CommonSwitch
				id={"additional-test-switch"}
				checked={lockUserField.checked}
				setChecked={lockUserField.setChecked}
			/>
		</div>
	);
	const addEditUserModalContent: React.ReactNode = (
		<div className="registration-input-content">
			{registrationInputs[0]}
			<div className="register-grid-row">
				{registrationInputs[1]}
				{registrationInputs[2]}
			</div>
			{registrationInputs[3]}
			{props.user && props.user.role && operatorCanEditUserAccess ? (
				lockUserSwitch
			) : (
				<></>
			)}
			{props.user && props.user.role && operatorCanEditUserAccess && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						gap: "5px",
					}}
				>
					<div className="typography-heading-03">
						{translation("ChangePassword")}
					</div>
					<CommonSwitch
						id={"user-change-password-switch"}
						checked={changePassword}
						setChecked={setChangePassword}
					/>
				</div>
			)}
			{(!props.user || changePassword) && (
				<>
					{passwordInputs[0]}
					{passwordInputs[1]}
				</>
			)}

			<CustomDropdown
				id="user-role-dropdown"
				label={translation("Role")}
				disabled={props.user && props.user.role && !operatorCanEditUserAccess}
				options={Object.values(SystemRole)
					.filter((type) => {
						return type !== SystemRole.SYSTEM_ADMIN;
					})
					.map((type: SystemRole) => {
						return {
							title: getUserRoleString(type, translation),
							value: type,
						};
					})}
				value={role}
				setValue={setRole}
			></CustomDropdown>
		</div>
	);

	let modalContent: React.ReactNode;

	switch (currentPageIndex) {
		case 0:
			modalContent = addEditUserModalContent;
			break;
		case 1:
			modalContent = (
				<EditAnalystPermissionModalContent
					company={props.company}
					newGroupId={newGroupId}
					customGroups={companyCustomGroups}
					permissionState={analystPermissions}
					toggleCheckboxOption={toggleCheckboxOption}
					toggleOtherTestOption={toggleOtherTestOption}
					changeCustomGroup={changeCustomGroup}
				/>
			);
			break;
	}

	const buttonText: string = props.user
		? translation("Edit")
		: translation("Create");

	const systemRolePermissions: Array<FrontendRolePermission> = props.company
		.systemRolePermissions as Array<FrontendRolePermission>;
	const matchingRole: FrontendRolePermission = systemRolePermissions.find(
		(sysRole) => {
			return sysRole.roleKey === role;
		}
	) as FrontendRolePermission;

	const addEditUserModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.user ? translation("EditUser") : translation("CreateUser")
			}
			headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						[
							BatchAccessLevel.LIMITED_ACCESS,
							BatchAccessLevel.ASSIGNED_ONLY,
						].includes(matchingRole?.accessLevel.limitedBatchAccess) &&
						currentPageIndex == 0
							? translation("SetPermissions")
							: buttonText
					}
					onClick={currentPageIndex == 0 ? handleSubmitUserInfo : handleSubmit}
				></GreenButton>,
			]}
		>
			{modalContent}
		</ModalWithButtons>
	);
	return addEditUserModal;
}
